import React from "react";
import { ConfirmationDialog } from "../../../assets/types";
import { FormatHelper } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { LoadingIndicator } from "../../../assets/vfx";
import { VfxDialogProps } from "../../../assets/vfx/dialog/vfxDialogProps";
import Drawer from "../../../assets/vfx/drawer/drawer";
import { RequestPushNotificationDto } from "../../../services/dto";
import DisplayData from "../../common/DisplayData/DisplayData";
import { QuestionnaireNotificationType } from "../../questionnaire/questionnaireForm/questionnaireContainer";
import { PushNotificationLookupProps } from "./pushNotificationForm";
import { isNewRequest } from "./pushNotificationPage";

interface PushNotificationViewProps {
  data?: RequestPushNotificationDto;
  show: boolean;
  onHide: () => void;
  onEdit: () => void;
  onCopy?: () => void;
  onCancel: () => void;
  lookups?: PushNotificationLookupProps;
}

export const PushNotificationView = (props: PushNotificationViewProps) => {
  const community: string | undefined = props.lookups?.communities.find(
    (x) => x.value === props.data?.fromCommunityId
  )?.display;

  const lnkPageType: string | undefined =
    props.data?.pageLinkId && props.data?.pageLinkId != Constants.emptyGuid
      ? props.lookups?.pageLink.find((x) => x.value === props.data?.pageLinkId)
        ?.display
      : undefined;

  const modalDialogProps: VfxDialogProps | undefined =
    new ConfirmationDialog().get(props.onCancel, "Cancel", {
      Name: "Push Notification",
    });

  const hasNewRequestStatus = isNewRequest(props.data?.status);
  const isQuestionnaireNotification =
    props.data?.notificationTypeId === QuestionnaireNotificationType.poll ||
    props.data?.notificationTypeId === QuestionnaireNotificationType.feedback;

  const communityAutoOptions = React.useMemo(
    () =>
      (props.lookups?.userCommunities ?? []).map((o) => ({
        id: String(o.value),
        name: String(o.display),
      })),
    [props.lookups?.userCommunities]
  );

  return (
    <Drawer.View
      className="w-30"
      show={props.show}
      backdrop={true}
      placement="end"
      scroll={true}
      onCopy={props.onCopy}
      onHide={props.onHide}
      onEdit={
        hasNewRequestStatus && !isQuestionnaireNotification
          ? props.onEdit
          : undefined
      }
      title={"View Push Notification"}
      onCancel={hasNewRequestStatus ? props.onCancel : undefined}
      modalDialogProps={hasNewRequestStatus ? modalDialogProps : undefined}
    >
      {!props.data ? (
        <LoadingIndicator isFullPage />
      ) : (
        <>
          <DisplayData text="Title" importedData={props.data?.title} />
          {props.data?.subtitle && (
            <DisplayData text="Subtitle" importedData={props.data?.subtitle} />
          )}
          <DisplayData text="Message" importedData={props.data?.message} />
          <DisplayData text="Community" importedData={community} />
          <DisplayData
            text="Community to Send To"
            importedData={props?.data?.sendToCommunityIds
              ?.map(
                (communityToSendId) =>
                  communityAutoOptions.find((x) => x.id === communityToSendId)
                    ?.name
              )
              .filter(Boolean)
              .map((name, index) => (
                <div key={index}>{name}</div>
              ))}
          />
          {props.data?.geometry && props.data.geometry.length > 0 && (
            <DisplayData
              text="Geometry"
              importedData={props.data.geometry
                ?.map(
                  (zipId) =>
                    props.lookups?.zipCodes?.find((x) => x.value === zipId)
                      ?.display
                )
                .filter(Boolean)
                .map((name, index) => (
                  <div key={index}>{name}</div>
                ))}
            />
          )}
          {props.data?.externalUrl && (
            <DisplayData
              text="Link Url"
              importedData={props.data?.externalUrl}
            />
          )}
          {lnkPageType && (
            <DisplayData text="Page Link" importedData={lnkPageType} />
          )}
          <DisplayData
            text="Scheduled Date"
            importedData={
              props.data?.scheduledTimeUtc
                ? FormatHelper.formatDate(props.data?.scheduledTimeUtc)
                : Constants.emptyString
            }
          />
          <DisplayData
            text="Quick Reply"
            importedData={props.data.quickReply ? "Yes" : "No"}
          />
        </>
      )}
    </Drawer.View>
  );
};
